import useFetch from '../../../hooks/useFetch';
import { useState } from 'react';
import { ServiceEvents } from '../shared/interfaces';
import { buildEndpoint } from '../EndPoints';
import { getFetchDefaultHeaders } from '../shared/vars';

/**
 * Estudios y abogados independientes: mi estudio, equipo, marca, correos,
 * cartera de clientes, suscripción, invitaciones y panel SaaS de Monere.
 */
const useOrganizationService = () => {
    const { doGet, doPost } = useFetch();

    const BASE_PATH = 'organizations';

    const [fetchingMyOrganization, setFetchingMyOrganization] = useState(false);
    const [fetchingUpdateOrganization, setFetchingUpdateOrganization] = useState(false);
    const [fetchingMembers, setFetchingMembers] = useState(false);
    const [fetchingInvite, setFetchingInvite] = useState(false);
    const [fetchingMemberAction, setFetchingMemberAction] = useState(false);
    const [fetchingBranding, setFetchingBranding] = useState(false);
    const [fetchingUpdateBranding, setFetchingUpdateBranding] = useState(false);
    const [fetchingEmailTemplates, setFetchingEmailTemplates] = useState(false);
    const [fetchingEmailTemplate, setFetchingEmailTemplate] = useState(false);
    const [fetchingSaveEmailTemplate, setFetchingSaveEmailTemplate] = useState(false);
    const [fetchingPreviewEmailTemplate, setFetchingPreviewEmailTemplate] = useState(false);
    const [fetchingClients, setFetchingClients] = useState(false);
    const [fetchingStoreClient, setFetchingStoreClient] = useState(false);
    const [fetchingClient, setFetchingClient] = useState(false);
    const [fetchingClientAction, setFetchingClientAction] = useState(false);
    const [fetchingPlans, setFetchingPlans] = useState(false);
    const [fetchingSubscription, setFetchingSubscription] = useState(false);
    const [fetchingInvitation, setFetchingInvitation] = useState(false);
    const [fetchingAcceptInvitation, setFetchingAcceptInvitation] = useState(false);
    const [fetchingAssignableLawyers, setFetchingAssignableLawyers] = useState(false);
    const [fetchingAdminOrganizations, setFetchingAdminOrganizations] = useState(false);
    const [fetchingAdminMetrics, setFetchingAdminMetrics] = useState(false);
    const [fetchingAdminOrganization, setFetchingAdminOrganization] = useState(false);
    const [fetchingAdminAction, setFetchingAdminAction] = useState(false);
    const [fetchingRegister, setFetchingRegister] = useState(false);
    const [fetchingDpa, setFetchingDpa] = useState(false);
    const [fetchingExport, setFetchingExport] = useState(false);
    const [fetchingClosure, setFetchingClosure] = useState(false);
    const [fetchingRoles, setFetchingRoles] = useState(false);
    const [fetchingSaveRole, setFetchingSaveRole] = useState(false);

    const toQuery = (filters: Record<string, unknown> = {}) =>
        Object.entries(filters)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
            .join('&');

    // ===== Mi estudio =====
    const getMyOrganization = (events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/my-organization`), setFetching: setFetchingMyOrganization });
    };

    const updateMyOrganization = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/my-organization/update`), body: data, setFetching: setFetchingUpdateOrganization });
    };

    // ===== Contrato de encargo y salida del estudio =====
    const acceptDataProcessing = (events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/data-processing/accept`), setFetching: setFetchingDpa });
    };

    /**
     * Descarga el ZIP con los datos del estudio. Es un archivo, no JSON: se usa
     * fetch directo para leer el nombre del Content-Disposition.
     */
    const exportOrganizationData = async (events: { onSuccess?: () => void; onError?: (message: string) => void } = {}) => {
        setFetchingExport(true);
        try {
            const response = await fetch(buildEndpoint(`${BASE_PATH}/export`), { headers: getFetchDefaultHeaders() });
            if (!response.ok) {
                let message = 'No se pudo descargar la exportación. Intenta nuevamente.';
                try {
                    const body = await response.json();
                    message = body?.message || message;
                } catch {
                    // respuesta sin JSON: se usa el mensaje genérico
                }
                events.onError?.(message);
                return;
            }
            const disposition = response.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
            const filename = match ? decodeURIComponent(match[1]) : 'monere-estudio.zip';
            const blobURL = URL.createObjectURL(await response.blob());
            const link = document.createElement('a');
            link.href = blobURL;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobURL), 1000);
            events.onSuccess?.();
        } catch {
            events.onError?.('No se pudo conectar con el servidor. Intenta de nuevo.');
        } finally {
            setFetchingExport(false);
        }
    };

    const closeOrganization = (data: { confirm_name: string; reason: string }, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/close`), body: data, requestType: 'json', setFetching: setFetchingClosure });
    };

    const cancelOrganizationClosure = (events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/close/cancel`), setFetching: setFetchingClosure });
    };

    // ===== Roles propios del estudio =====
    const getRoles = (events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/roles`), setFetching: setFetchingRoles });
    };

    const storeRole = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/roles/store`), body: data, requestType: 'json', setFetching: setFetchingSaveRole });
    };

    const updateRole = (id: number, data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/roles/${id}/update`), body: data, requestType: 'json', setFetching: setFetchingSaveRole });
    };

    const deleteRole = (id: number, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/roles/${id}/delete`), setFetching: setFetchingSaveRole });
    };

    // ===== Equipo =====
    const getMembers = (events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/members`), setFetching: setFetchingMembers });
    };

    const inviteMember = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/members/invite`), body: data, setFetching: setFetchingInvite });
    };

    const resendInvitation = (memberId: number, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/members/${memberId}/resend`), setFetching: setFetchingMemberAction });
    };

    const changeMemberRole = (memberId: number, role: string, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/members/${memberId}/change-role`), body: { role }, setFetching: setFetchingMemberAction });
    };

    const removeMember = (memberId: number, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/members/${memberId}/remove`), setFetching: setFetchingMemberAction });
    };

    const getAssignableLawyers = (events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/assignable-lawyers`), setFetching: setFetchingAssignableLawyers });
    };

    // ===== Marca =====
    const getBranding = (events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/branding`), setFetching: setFetchingBranding });
    };

    const updateBranding = (data: FormData, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/branding/update`), body: data, setFetching: setFetchingUpdateBranding });
    };

    // ===== Correos del estudio =====
    const getEmailTemplates = (events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/email-templates`), setFetching: setFetchingEmailTemplates });
    };

    const getEmailTemplate = (key: string, events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/email-templates/${key}`), setFetching: setFetchingEmailTemplate });
    };

    const previewEmailTemplate = (key: string, data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/email-templates/${key}/preview`), body: data, setFetching: setFetchingPreviewEmailTemplate });
    };

    const updateEmailTemplate = (key: string, data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/email-templates/${key}/update`), body: data, setFetching: setFetchingSaveEmailTemplate });
    };

    const resetEmailTemplate = (key: string, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/email-templates/${key}/reset`), setFetching: setFetchingSaveEmailTemplate });
    };

    // ===== Cartera de clientes =====
    const getClients = (filters: Record<string, unknown> = {}, events: ServiceEvents = {}) => {
        doGet({ ...events, url: `${buildEndpoint(`${BASE_PATH}/clients`)}?${toQuery(filters)}`, setFetching: setFetchingClients });
    };

    const storeClient = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/clients/store`), body: data, setFetching: setFetchingStoreClient });
    };

    const showClient = (id: number, events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/clients/${id}/show`), setFetching: setFetchingClient });
    };

    const archiveClient = (id: number, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/clients/${id}/archive`), setFetching: setFetchingClientAction });
    };

    const restoreClient = (id: number, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/clients/${id}/restore`), setFetching: setFetchingClientAction });
    };

    const resendClientWelcome = (id: number, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/clients/${id}/resend-welcome`), setFetching: setFetchingClientAction });
    };

    // ===== Suscripción del estudio =====
    const getOrganizationPlans = (events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/subscription/plans`), setFetching: setFetchingPlans });
    };

    const createSubscription = (planId: number, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/subscription/create`), body: { plan_id: planId }, setFetching: setFetchingSubscription });
    };

    const completeSubscription = (events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/subscription/complete`), setFetching: setFetchingSubscription });
    };

    const cancelSubscription = (events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/subscription/cancel`), setFetching: setFetchingSubscription });
    };

    // ===== Público: registro e invitaciones =====
    const getPublicPlans = (organizationType: string | null, events: ServiceEvents = {}) => {
        const query = organizationType ? `?organization_type=${organizationType}` : '';
        doGet({ ...events, url: `${buildEndpoint(`${BASE_PATH}/public/plans`)}${query}`, setFetching: setFetchingPlans });
    };

    const registerLawyer = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint('auths/public-register-lawyer'), body: data, setFetching: setFetchingRegister });
    };

    const getInvitation = (token: string, events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/invitations/${token}`), setFetching: setFetchingInvitation });
    };

    // Invitación a la cartera de un estudio (para quien ya tenía cuenta)
    const getClientInvitation = (token: string, events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/client-invitations/${token}`), setFetching: setFetchingInvitation });
    };

    const respondClientInvitation = (token: string, action: 'accept' | 'decline', events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/client-invitations/${token}/${action}`), setFetching: setFetchingAcceptInvitation });
    };

    const acceptInvitation = (token: string, data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/invitations/${token}/accept`), body: data, setFetching: setFetchingAcceptInvitation });
    };

    // ===== Panel SaaS de Monere =====
    const getAdminOrganizations = (filters: Record<string, unknown> = {}, events: ServiceEvents = {}) => {
        doGet({ ...events, url: `${buildEndpoint(`${BASE_PATH}/admin`)}?${toQuery(filters)}`, setFetching: setFetchingAdminOrganizations });
    };

    const getAdminMetrics = (events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/admin/metrics`), setFetching: setFetchingAdminMetrics });
    };

    const getAdminOrganization = (id: number, events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/admin/${id}/show`), setFetching: setFetchingAdminOrganization });
    };

    const suspendOrganization = (id: number, reason: string, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/admin/${id}/suspend`), body: { reason }, setFetching: setFetchingAdminAction });
    };

    const reactivateOrganization = (id: number, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/admin/${id}/reactivate`), setFetching: setFetchingAdminAction });
    };

    const assignPlanToOrganization = (id: number, data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/admin/${id}/assign-plan`), body: data, setFetching: setFetchingAdminAction });
    };

    // Monere invita a alguien a un estudio en su nombre
    const inviteToOrganizationAdmin = (id: number, data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/admin/${id}/invite`), body: data, setFetching: setFetchingAdminAction });
    };

    // Abogado con cuenta previa → abogado independiente o estudio
    const previewConvertLawyer = (email: string, events: ServiceEvents = {}) => {
        doGet({ ...events, url: buildEndpoint(`${BASE_PATH}/admin/convert-lawyer?email=${encodeURIComponent(email)}`), setFetching: setFetchingAdminAction });
    };

    const convertLawyer = (data: Record<string, unknown>, events: ServiceEvents = {}) => {
        doPost({ ...events, url: buildEndpoint(`${BASE_PATH}/admin/convert-lawyer`), body: data, setFetching: setFetchingAdminAction });
    };

    return {
        fetchingMyOrganization, fetchingUpdateOrganization, fetchingMembers, fetchingInvite, fetchingMemberAction,
        fetchingBranding, fetchingUpdateBranding, fetchingEmailTemplates, fetchingEmailTemplate,
        fetchingSaveEmailTemplate, fetchingPreviewEmailTemplate, fetchingClients, fetchingStoreClient,
        fetchingClient, fetchingClientAction, fetchingPlans, fetchingSubscription, fetchingInvitation,
        fetchingAcceptInvitation, fetchingAssignableLawyers, fetchingAdminOrganizations, fetchingAdminMetrics,
        fetchingAdminOrganization, fetchingAdminAction, fetchingRegister,
        fetchingDpa, fetchingExport, fetchingClosure, fetchingRoles, fetchingSaveRole,
        acceptDataProcessing, exportOrganizationData, closeOrganization, cancelOrganizationClosure,
        getRoles, storeRole, updateRole, deleteRole,
        getMyOrganization, updateMyOrganization,
        getMembers, inviteMember, resendInvitation, changeMemberRole, removeMember, getAssignableLawyers,
        getBranding, updateBranding,
        getEmailTemplates, getEmailTemplate, previewEmailTemplate, updateEmailTemplate, resetEmailTemplate,
        getClients, storeClient, showClient, archiveClient, restoreClient, resendClientWelcome,
        getOrganizationPlans, createSubscription, completeSubscription, cancelSubscription,
        getPublicPlans, registerLawyer, getInvitation, acceptInvitation, getClientInvitation, respondClientInvitation,
        fetchingClientInvitation: fetchingInvitation,
        getAdminOrganizations, getAdminMetrics, getAdminOrganization, suspendOrganization,
        reactivateOrganization, assignPlanToOrganization, inviteToOrganizationAdmin, previewConvertLawyer, convertLawyer
    };
};

export default useOrganizationService;
